import './fonts/fonts.css'
import './styles/tokens.css'

import { createTheme } from '@mui/material'
import { newThemeObject } from './newTheme'
import { tokens } from './styles/tokens'

function buildThemeObject() {
  return {
    palette: {
      primary: {
        main: newThemeObject.palette.main.primary,
        dark: tokens.color.primary[800],
        light: tokens.color.primary[400],
        mainWhite: tokens.color.white,
        contrastMainWhite: tokens.color.neutral[800],
      },
      secondary: {
        // Pozor: '#fff' zde využívá useOrderTableRowColor pro bílé řádky.
        main: '#fff',
        darklight: '#E6D815',
        mustsee: '#C6BDE9',
      },
      background: {
        default: newThemeObject.palette.main.background,
      },
      text: {
        primary: tokens.color.neutral[900],
        secondary: tokens.color.neutral[500],
      },
      // Tabulkové bordery čerpají z grey[300-600]; mapováno o stupeň
      // světleji na neutrální škálu, aby bordery byly jemnější.
      grey: {
        50: tokens.color.neutral[50],
        100: tokens.color.neutral[100],
        200: tokens.color.neutral[200],
        300: tokens.color.neutral[200],
        400: tokens.color.neutral[300],
        500: tokens.color.neutral[400],
        600: tokens.color.neutral[500],
        700: tokens.color.neutral[700],
        800: tokens.color.neutral[800],
        900: tokens.color.neutral[900],
      },
      error: {
        main: tokens.color.error[500],
      },
      warning: {
        main: tokens.color.warning[500],
      },
      success: {
        main: tokens.color.success[500],
      },
      buy: {
        main: tokens.color.primary[500],
      },
    },

    typography: {
      fontSize: 12,
      fontWeightRegular: tokens.font.weight.regular,
      fontWeightMedium: tokens.font.weight.medium,
      fontWeightBold: tokens.font.weight.semibold,
      fontWeightLight: 300,
      fontFamily: tokens.font.family,
      // Nadpisy (dialogy, stránky) čerpají ze samostatného display fontu —
      // v luxusním vzhledu serifové písmo, jinde shodné s běžným textem.
      h1: { fontFamily: tokens.font.display },
      h2: { fontFamily: tokens.font.display },
      h3: { fontFamily: tokens.font.display },
      h4: { fontFamily: tokens.font.display },
      h5: { fontFamily: tokens.font.display, letterSpacing: '-0.01em' },
      h6: { fontFamily: tokens.font.display, letterSpacing: '-0.01em' },
    },
  }
}

function buildMuiTheme() {
  return createTheme({
    ...buildThemeObject(),
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: tokens.color.neutral[50],
            color: tokens.color.neutral[900],
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          // Viditelný focus ring pro klávesnici, myš ring nevyvolá.
          '*:focus-visible': {
            outline: tokens.focus.ring,
            outlineOffset: tokens.focus.ringOffset,
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              transitionDuration: '0.01ms !important',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
            fontWeight: tokens.font.weight.medium,
            borderRadius: tokens.radius.md,
            boxShadow: 'none',
            transition: `background-color ${tokens.transition.fast}, box-shadow ${tokens.transition.base}, color ${tokens.transition.fast}`,
            '&:hover': {
              boxShadow: 'none',
            },
            '&:focus-visible': {
              outline: tokens.focus.ring,
              outlineOffset: tokens.focus.ringOffset,
            },
            '&.Mui-disabled': {
              opacity: 0.45,
            },
          },
          containedPrimary: {
            backgroundColor: tokens.color.primary[700],
            '&:hover': {
              backgroundColor: tokens.color.primary[800],
            },
            '&:active': {
              backgroundColor: tokens.color.primary[900],
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: `background-color ${tokens.transition.fast}`,
            '&:focus-visible': {
              outline: tokens.focus.ring,
              outlineOffset: tokens.focus.ringOffset,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.md,
            backgroundColor: tokens.color.white,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.color.neutral[300],
              transition: `border-color ${tokens.transition.fast}`,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.color.neutral[400],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.color.primary[500],
              borderWidth: 1,
              boxShadow: tokens.focus.shadow,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: tokens.color.neutral[200],
          },
          head: {
            color: tokens.color.neutral[600],
            fontWeight: tokens.font.weight.semibold,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: tokens.radius.lg,
            boxShadow: tokens.shadow.lg,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: tokens.radius.md,
            boxShadow: tokens.shadow.md,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: tokens.color.neutral[800],
            borderRadius: tokens.radius.sm,
            fontSize: '0.7rem',
          },
        },
      },
    },
  })
}

export let themeObject = buildThemeObject()
export let muiTheme = buildMuiTheme()

// Znovu sestaví MUI theme z aktuální (případně právě přepnuté) barevné
// palety tokenů — volá contexts/ThemeModeContext.tsx při přepnutí vzhledu.
export function rebuildMuiTheme() {
  themeObject = buildThemeObject()
  muiTheme = buildMuiTheme()
  return muiTheme
}
